import { useState } from 'react';

// First-sign-in walkthrough for staff (Regional / Project / Mobile Unit
// Managers). Opens automatically once per person per browser — the "seen"
// flag is a per-viewer convenience in localStorage, so clearing site data or
// using a new browser just shows it again, which is harmless. The
// "How it works" link in the top bar reopens it any time.

export const FIRST_TIME_TOUR_ROLES = ['regional_manager', 'project_manager', 'unit_manager', 'account_manager', 'finance_manager'];
const FLEET_SCOPED_ROLES = ['regional_manager', 'project_manager', 'unit_manager', 'account_manager'];

const ROLE_LABELS = {
  admin: 'Admin',
  regional_manager: 'Regional Manager',
  project_manager: 'Project Manager',
  unit_manager: 'Mobile Unit Manager',
  account_manager: 'Account Manager',
  finance_manager: 'Financial Manager',
};

export const tourSeenKey = (user) => `staffTourSeen:${(user?.email || 'unknown').toLowerCase()}`;

export function hasSeenTour(user) {
  try { return localStorage.getItem(tourSeenKey(user)) === '1'; } catch { return true; }
}

function markTourSeen(user) {
  try { localStorage.setItem(tourSeenKey(user), '1'); } catch {}
}

function buildSteps(role) {
  const isRM = role === 'regional_manager';
  const isAdmin = role === 'admin';
  const isFinance = role === 'finance_manager';
  const fleetScoped = FLEET_SCOPED_ROLES.includes(role);

  const steps = [
    {
      title: `Welcome to the Talent Pool${ROLE_LABELS[role] ? `, ${ROLE_LABELS[role]}` : ''}`,
      body: [
        'This is where Guud Vision tracks every Optometrist and Optical Dispenser who applies — from first application through to being placed on a mobile.',
        'This quick tour shows you where everything is. It takes about a minute, and you can reopen it any time from "How it works" in the top bar.',
      ],
    },
    {
      title: 'The top bar',
      body: [
        'Dashboard — your list of candidates. This is where you\'ll spend most of your time.',
        'Applications — capture a new candidate yourself.',
        'Scorecard — interview scorecards for candidates.',
        '⌂ Portal Home — back to the main Guud Vision portal.',
        '🔔 The bell shows notifications, e.g. when a candidate uploads a document or their status changes.',
      ],
    },
    {
      title: 'Finding a candidate',
      body: [
        'The Dashboard lists candidates with their position and current status.',
        'Click a candidate to open their full profile.',
        fleetScoped
          ? 'You\'ll see every candidate who is still in the hiring process. Once someone is Placed, they only stay on your list if they were placed on one of your assigned fleets.'
          : `${isFinance ? 'You see' : 'Admins see'} every candidate, at every stage.`,
      ],
    },
    {
      title: 'Watch: viewing a candidate profile',
      body: ['A short walkthrough of opening and reading a candidate\'s profile.'],
      video: '/videos/viewing-candidate-profiles.mp4',
    },
    {
      title: 'Where a candidate is in the process',
      body: [
        'Each candidate has a status badge at the top of their profile:',
        'Registered → Under review → Compliance → Short listed → Offer Letter Sent → Offer Letter Accepted → Placed (or Rejected).',
        'Short listed means the candidate has been recommended and is ready for a hiring decision.',
      ],
    },
    {
      title: 'The candidate profile',
      body: [
        'Personal details, position, availability and references are on the profile page.',
        'Documents — CV / Resume, ID, Driver\'s Licence, HPCSA registration, CPD certificate, Degree and Salary Slip. A ✓ means it\'s uploaded, ✗ means it\'s still awaited. Click a document to open it.',
        'Request 2nd Interview (top right) prepares a second-interview request for that candidate.',
        isAdmin || isRM
          ? 'You can see salary expectations and edit the profile.'
          : isFinance
            ? 'You can see salaries, but your access is view-only — nothing can be changed.'
            : 'Salary details are hidden for your role.',
      ],
    },
  ];

  if (isRM) {
    steps.push({
      title: 'Ready to hire? Use "Let\'s hire"',
      body: [
        'When you open a Short listed candidate you\'ll see a "Let\'s hire" button at the top right of their profile.',
        'Click it and HR is emailed straight away that you want to hire this person. The candidate\'s status doesn\'t change — HR sends the offer letter through the normal process.',
      ],
    });
  }

  steps.push({
    title: 'Good to know',
    body: [
      'For security you\'re signed out automatically after 10 minutes without activity.',
      'Candidate information is confidential (POPIA) — please don\'t download or share it outside of Guud Vision.',
      'Stuck or something looks wrong? Contact bernike@guudvision.health.',
    ],
  });

  return steps;
}

export default function StaffWelcomeTour({ user, onClose }) {
  const steps = buildSteps(user?.role);
  const [i, setI] = useState(0);
  const step = steps[i];
  const isLast = i === steps.length - 1;

  const close = () => { markTourSeen(user); onClose(); };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="staff-tour-title"
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1000, display: 'grid', placeItems: 'center', padding: 16 }}
    >
      <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 12, width: '100%', maxWidth: 640, maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 16px 48px rgba(0,0,0,0.35)', overflow: 'hidden' }}>
        <div style={{ padding: '22px 24px 8px' }}>
          <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 6 }}>Step {i + 1} of {steps.length}</div>
          <h2 id="staff-tour-title" style={{ margin: '0 0 12px', fontSize: 19, color: 'var(--text)' }}>{step.title}</h2>
          <ul style={{ margin: 0, paddingLeft: 18, color: 'var(--text2)', fontSize: 14, lineHeight: 1.55 }}>
            {step.body.map((line, n) => <li key={n} style={{ marginBottom: 6 }}>{line}</li>)}
          </ul>
          {step.video && (
            <video
              key={step.video}
              src={step.video}
              controls
              playsInline
              preload="metadata"
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
              style={{ width: '100%', borderRadius: 8, background: '#000', marginTop: 10 }}
            >
              Your browser cannot play this video.
            </video>
          )}
        </div>

        {/* progress dots */}
        <div style={{ display: 'flex', gap: 6, justifyContent: 'center', padding: '10px 0 4px' }}>
          {steps.map((_, n) => (
            <span key={n} style={{ width: 8, height: 8, borderRadius: '50%', background: n === i ? 'var(--accent)' : 'var(--border)' }} />
          ))}
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 24px 20px', gap: 8 }}>
          <button type="button" className="btn btn-ghost btn-sm" onClick={close}>Skip tour</button>
          <div style={{ display: 'flex', gap: 8 }}>
            {i > 0 && <button type="button" className="btn btn-ghost btn-sm" onClick={() => setI(i - 1)}>Back</button>}
            <button type="button" className="btn btn-primary btn-sm" onClick={() => (isLast ? close() : setI(i + 1))}>
              {isLast ? 'Got it' : 'Next'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
